import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Subscribes to realtime changes on the given tables and re-runs `onChange`
 * whenever any insert/update/delete happens. Also refetches when the tab
 * regains focus or visibility, so the UI always reflects what's in Supabase.
 *
 * Channel names follow `refetch-<uid>-...` so realtime RLS policies can scope
 * subscriptions to the owning user only.
 */
export function useRealtimeRefetch(tables: string[], onChange: () => void) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const onVisible = () => {
      if (document.visibilityState === 'visible') onChangeRef.current();
    };
    const onFocus = () => onChangeRef.current();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled || !user) return;

      channel = supabase.channel(
        `refetch-${user.id}-${tables.join('-')}-${Math.random().toString(36).slice(2, 8)}`
      );
      tables.forEach((t) => {
        channel!.on(
          'postgres_changes' as any,
          { event: '*', schema: 'public', table: t },
          () => onChangeRef.current()
        );
      });
      channel.subscribe();
    })();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables.join('|')]);
}
